(function () {
  'use strict';

  // Shiprocket Smart Cart ignores the `_bundle` line item property entirely —
  // its own grouped-set UI is driven by `_bundleItems` injected server-side, and
  // its generic property list skips any key containing an underscore. So the set
  // header has to be drawn here, against the Smart Cart's shadow DOM.

  var ACCENT = '#D4789E';
  var ACCENT_SOFT = '#F3D5E0';
  var STYLE_ID = 'hummara-bundle-group-style';
  var HEADER_CLASS = 'hummara-bundle-header';
  var ITEM_CLASS = 'hummara-bundle-item';
  var LAST_CLASS = 'hummara-bundle-item--last';
  var CART_TTL = 1200;

  var cartPromise = null;
  var cartFetchedAt = 0;
  var lineToBundle = {};
  var lineQty = {};
  var bundleLines = {};
  var observer = null;
  var repaintTimer = null;
  var boundRoot = null;
  var modal = null;
  var pendingBundle = null;

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  function escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, function (ch) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch];
    });
  }

  function getShadow() {
    var host = document.querySelector('[data-app="shiprocket-smart-cart"]');
    return host && host.shadowRoot ? host.shadowRoot : null;
  }

  function getCart() {
    var now = Date.now();
    if (cartPromise && now - cartFetchedAt < CART_TTL) return cartPromise;
    cartFetchedAt = now;
    cartPromise = fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .catch(function () {
        return null;
      });
    return cartPromise;
  }

  function invalidateCart() {
    cartFetchedAt = 0;
  }

  function injectStyle(root) {
    if (root.getElementById && root.getElementById(STYLE_ID)) return;
    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent =
      '.' + HEADER_CLASS + '{' +
      'display:flex;align-items:baseline;justify-content:space-between;gap:10px;flex-wrap:wrap;' +
      'margin:14px 0 0;padding:11px 14px;' +
      'border:1px solid ' + ACCENT_SOFT + ';border-bottom:none;border-radius:14px 14px 0 0;' +
      'background:linear-gradient(135deg,rgba(212,120,158,.12),rgba(212,120,158,.03));' +
      'font-size:12.5px;font-weight:700;letter-spacing:.05em;text-transform:uppercase;' +
      'color:' + ACCENT + ';}' +
      '.' + HEADER_CLASS + ' .hbh-count{' +
      'font-size:11px;font-weight:600;letter-spacing:.01em;text-transform:none;color:#8B7355;}' +
      '.' + ITEM_CLASS + '{' +
      'border-left:3px solid ' + ACCENT + ';border-right:1px solid ' + ACCENT_SOFT + ';' +
      'background:rgba(212,120,158,.025);padding-left:7px;margin-top:0;}' +
      '.' + LAST_CLASS + '{' +
      'border-bottom:1px solid ' + ACCENT_SOFT + ';border-radius:0 0 14px 14px;margin-bottom:6px;}';
    root.appendChild(style);
  }

  function buildGroups(cart) {
    var groups = {};
    var names = [];
    toArray(cart && cart.items).forEach(function (item) {
      var name = item.properties && item.properties._bundle;
      if (!name) return;
      if (!groups[name]) {
        groups[name] = [];
        names.push(name);
      }
      groups[name].push(item);
    });
    return { groups: groups, names: names };
  }

  // Prefer the Shopify line key (`variantId:hash`). Falling back to variant id
  // alone is what pulled a homepage add of the same product into the set.
  function rowKey(row) {
    var ids = toArray(row.querySelectorAll('[data-id]'))
      .map(function (el) {
        return el.getAttribute('data-id');
      })
      .filter(Boolean);
    for (var i = 0; i < ids.length; i++) {
      if (String(ids[i]).indexOf(':') !== -1) return ids[i];
    }
    return ids[0] || null;
  }

  function bundleNameOf(item) {
    return (item && item.properties && item.properties._bundle) || '';
  }

  function assignCartItemsToRows(rows, cartItems) {
    var assigned = new Array(rows.length);
    var used = {};

    function take(item, idx) {
      assigned[idx] = item;
      used[item.key] = true;
    }

    var keysAgree = rows.length === cartItems.length && cartItems.length > 0;
    if (keysAgree) {
      rows.forEach(function (row, idx) {
        var key = rowKey(row);
        if (key && key.indexOf(':') !== -1 && key !== cartItems[idx].key) keysAgree = false;
      });
    }
    if (keysAgree) {
      rows.forEach(function (row, idx) {
        take(cartItems[idx], idx);
      });
      return assigned;
    }

    rows.forEach(function (row, idx) {
      var key = rowKey(row);
      if (!key || key.indexOf(':') === -1) return;
      var item = cartItems.filter(function (line) {
        return line.key === key;
      })[0];
      if (item) take(item, idx);
    });

    rows.forEach(function (row, idx) {
      if (assigned[idx]) return;
      var key = rowKey(row);
      if (!key) return;
      var variantId = String(key).split(':')[0];
      var item = cartItems.filter(function (line) {
        return !used[line.key] && String(line.variant_id) === variantId && bundleNameOf(line);
      })[0];
      if (item) take(item, idx);
    });

    return assigned;
  }

  function clearDecoration(root) {
    toArray(root.querySelectorAll('.' + HEADER_CLASS)).forEach(function (el) {
      el.parentNode.removeChild(el);
    });
    toArray(root.querySelectorAll('.' + ITEM_CLASS + ', [data-hummara-bundle]')).forEach(function (el) {
      el.classList.remove(ITEM_CLASS, LAST_CLASS);
      el.removeAttribute('data-hummara-bundle');
      el.removeAttribute('data-hummara-qty');
    });
  }

  function paint(root, cart) {
    clearDecoration(root);
    lineToBundle = {};
    lineQty = {};
    bundleLines = {};

    var cartItems = toArray(cart && cart.items);
    var built = buildGroups(cart);
    built.names.forEach(function (name) {
      bundleLines[name] = built.groups[name].map(function (item) {
        return item.key;
      });
    });
    if (!built.names.length) return;

    injectStyle(root);
    var rows = toArray(root.querySelectorAll('.mini-cart-item'));
    var assigned = assignCartItemsToRows(rows, cartItems);
    var grouped = {};

    rows.forEach(function (row, idx) {
      var item = assigned[idx];
      var name = bundleNameOf(item);
      if (!name) return;
      row.setAttribute('data-hummara-bundle', name);
      row.setAttribute('data-hummara-qty', String(item.quantity));
      lineToBundle[item.key] = name;
      lineQty[item.key] = item.quantity;
      if (!grouped[name]) grouped[name] = [];
      grouped[name].push({ row: row, item: item });
    });

    built.names.forEach(function (name) {
      var matched = grouped[name] || [];
      if (!matched.length) return;

      var count = 0;
      matched.forEach(function (entry) {
        count += entry.item.quantity;
        entry.row.classList.add(ITEM_CLASS);
      });
      matched[matched.length - 1].row.classList.add(LAST_CLASS);

      var anchor = matched[0].row.closest('.mini-cart-item-wrapper') || matched[0].row;
      var header = document.createElement('div');
      header.className = HEADER_CLASS;
      header.setAttribute('data-bundle-name', name);
      header.innerHTML =
        '<span>\u2726 ' + escapeHtml(name) + '</span>' +
        '<span class="hbh-count">' + count + ' item' + (count === 1 ? '' : 's') + ' included</span>';
      anchor.parentNode.insertBefore(header, anchor);
    });
  }

  function isPlusControl(control, row) {
    var next = parseInt(control.getAttribute('data-quantity'), 10);
    var current = parseInt(row.getAttribute('data-hummara-qty'), 10);
    if (!isNaN(next) && !isNaN(current)) return next > current;
    var label = (
      (control.getAttribute('aria-label') || '') +
      ' ' +
      (control.textContent || '') +
      ' ' +
      String(control.className || '')
    ).toLowerCase();
    return /plus|increase|increment|\+/.test(label);
  }

  // A part-changed set still bills at the set price, so + / − / delete on any
  // line in a bundle has to take the whole set with it. A homepage add of the
  // same product is a different line (no `_bundle`) and must not use this.
  function onControlClick(event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var deleteButton = target.closest('.mini-cart-item-delete, .delete-item');
    var qtyButton = target.closest('.mini-cart-qty-btn');
    var control = deleteButton || qtyButton;
    if (!control) return;

    var row = target.closest('.mini-cart-item');
    var name = row && row.getAttribute('data-hummara-bundle');
    if (!name) return;

    if (!deleteButton && !isPlusControl(control, row)) {
      var next = parseInt(control.getAttribute('data-quantity'), 10);
      var current = parseInt(row.getAttribute('data-hummara-qty'), 10);
      if (isNaN(next) || next >= current) return;
    }

    event.preventDefault();
    event.stopPropagation();
    if (event.stopImmediatePropagation) event.stopImmediatePropagation();
    confirmRemoveSet(name);
  }

  function refreshCartUI() {
    invalidateCart();
    if (typeof window.updateMiniCartSection === 'function') {
      try {
        var maybePromise = window.updateMiniCartSection();
        if (maybePromise && typeof maybePromise.then === 'function') {
          maybePromise.catch(function () {});
        }
      } catch (e) {}
    }
    try {
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    } catch (e) {}
    schedulePaint();
  }

  function removeSet(name) {
    var keys = bundleLines[name] || [];
    if (!keys.length) return;

    var updates = {};
    keys.forEach(function (key) {
      updates[key] = 0;
    });

    fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: updates }),
    })
      .then(refreshCartUI)
      .catch(function () {});
  }

  function buildModal() {
    if (modal) return modal;

    modal = document.createElement('div');
    modal.className = 'hummara-set-modal';
    modal.innerHTML =
      '<div class="hsm__card" role="dialog" aria-modal="true" aria-labelledby="hsm-title">' +
      '<div class="hsm__icon">\u2726</div>' +
      '<h3 class="hsm__title" id="hsm-title">Remove the whole set?</h3>' +
      '<p class="hsm__body">Your <strong class="hsm__name"></strong> is priced as one set, ' +
      'so removing a single piece removes all of them.</p>' +
      '<div class="hsm__actions">' +
      '<button type="button" class="hsm__keep">Keep my set</button>' +
      '<button type="button" class="hsm__confirm">Remove set</button>' +
      '</div></div>';

    var style = document.createElement('style');
    style.textContent =
      '.hummara-set-modal{position:fixed;inset:0;z-index:2147483647;display:flex;align-items:center;' +
      'justify-content:center;padding:24px;background:rgba(74,55,40,.45);backdrop-filter:blur(2px);' +
      'opacity:0;visibility:hidden;transition:opacity .22s ease,visibility .22s ease;}' +
      '.hummara-set-modal.is-open{opacity:1;visibility:visible;}' +
      '.hsm__card{background:#FFF9F7;border-radius:24px;padding:28px 24px 22px;max-width:340px;width:100%;' +
      'text-align:center;box-shadow:0 30px 60px -20px rgba(0,0,0,.35);transform:translateY(16px) scale(.97);' +
      'transition:transform .22s cubic-bezier(.22,1,.36,1);}' +
      '.hummara-set-modal.is-open .hsm__card{transform:none;}' +
      '.hsm__icon{font-size:26px;color:' + ACCENT + ';margin-bottom:8px;}' +
      '.hsm__title{font-size:19px;font-weight:600;color:#1a1a1a;margin:0 0 10px;}' +
      '.hsm__body{font-size:13.5px;line-height:1.55;color:#6b6b6b;margin:0 0 22px;}' +
      '.hsm__body strong{color:' + ACCENT + ';}' +
      '.hsm__actions{display:flex;flex-direction:column;gap:10px;}' +
      '.hsm__actions button{border-radius:999px;padding:12px 18px;font-size:14px;font-weight:600;' +
      'cursor:pointer;border:1px solid transparent;}' +
      '.hsm__keep{background:' + ACCENT + ';color:#fff;}' +
      '.hsm__confirm{background:transparent;color:#8B7355;border-color:' + ACCENT_SOFT + ';}';

    document.head.appendChild(style);
    document.body.appendChild(modal);

    modal.querySelector('.hsm__keep').addEventListener('click', closeModal);
    modal.querySelector('.hsm__confirm').addEventListener('click', function () {
      var name = pendingBundle;
      closeModal();
      if (name) removeSet(name);
    });
    modal.addEventListener('click', function (event) {
      if (event.target === modal) closeModal();
    });

    return modal;
  }

  function confirmRemoveSet(name) {
    var el = buildModal();
    pendingBundle = name;
    el.querySelector('.hsm__name').textContent = name;
    requestAnimationFrame(function () {
      el.classList.add('is-open');
    });
  }

  function closeModal() {
    pendingBundle = null;
    if (modal) modal.classList.remove('is-open');
  }

  function schedulePaint() {
    clearTimeout(repaintTimer);
    repaintTimer = setTimeout(run, 180);
  }

  function run() {
    var root = getShadow();
    if (!root) return;

    if (boundRoot !== root) {
      boundRoot = root;
      root.addEventListener('click', onControlClick, true);
    }

    getCart().then(function (cart) {
      if (!cart) return;
      if (observer) observer.disconnect();
      try {
        paint(root, cart);
      } catch (e) {}
      if (observer) observer.observe(root, { childList: true, subtree: true });
    });
  }

  function start() {
    var root = getShadow();
    if (!root) {
      setTimeout(start, 400);
      return;
    }

    observer = new MutationObserver(schedulePaint);
    observer.observe(root, { childList: true, subtree: true });

    document.addEventListener('cart:refresh', function () {
      invalidateCart();
      schedulePaint();
    });

    run();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
